import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, PawPrint } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Marquee from "react-fast-marquee";

const LINKS = [
  { to: "/", label: "Accueil" },
  { to: "/methode", label: "La Méthode" },
  { to: "/prestations", label: "Prestations" },
  { to: "/a-propos", label: "À propos" },
  { to: "/structures", label: "Structures & pros" },
];

const BANNER_ITEMS = [
  "Accompagnement à la parentalité",
  "Médiation animale",
  "Éducation canine",
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <header
      data-testid="site-navbar"
      className="fixed inset-x-0 top-0 z-50 border-b border-stone-border/70 bg-cream/80 backdrop-blur-xl"
    >
      <div data-testid="top-banner" className="overflow-hidden bg-terracotta py-2.5" aria-hidden="true">
        <Marquee speed={28} gradient={false}>
          {BANNER_ITEMS.map((item) => (
            <span key={item} className="mx-7 flex items-center gap-7">
              <span className="text-xs font-semibold uppercase tracking-[0.22em] text-cream">{item}</span>
              <PawPrint size={12} className="text-sand" />
            </span>
          ))}
        </Marquee>
      </div>
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 sm:px-10">
        <Link to="/" data-testid="nav-logo" className="group flex items-center gap-2.5" onClick={() => setOpen(false)}>
          <span className="block h-11 w-11 overflow-hidden rounded-full shadow-sm transition-transform duration-300 group-hover:rotate-6 group-hover:scale-105">
            <img
              src="/logo.png"
              alt="Logo L'effet poilus"
              className="h-full w-full scale-[2.4] object-cover"
              style={{ transformOrigin: "50% 30%" }}
            />
          </span>
          <span className="flex flex-col leading-tight">
            <span className="font-serif text-2xl font-semibold italic tracking-tight text-ink">
              L'effet poilus
            </span>
            <span className="hidden text-[10px] font-semibold uppercase tracking-[0.16em] text-terracotta sm:block">
              Le coaching familial à l'aide de l'animal
            </span>
          </span>
        </Link>

        <div className="hidden items-center gap-8 lg:flex">
          {LINKS.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              data-testid={`nav-link-${link.label.toLowerCase().replace(/[^a-zà-ÿ]+/g, "-")}`}
              className={({ isActive }) =>
                `relative text-sm font-medium uppercase tracking-[0.14em] transition-colors duration-300 ${
                  isActive ? "text-terracotta" : "text-ink/70 hover:text-ink"
                }`
              }
            >
              {link.label}
            </NavLink>
          ))}
          <Link
            to="/contact"
            data-testid="nav-contact-cta"
            className={`rounded-full px-6 py-2.5 text-sm font-semibold uppercase tracking-[0.14em] transition-[background-color,color,transform] duration-300 hover:-translate-y-0.5 active:scale-95 ${
              location.pathname === "/contact"
                ? "bg-forest text-cream"
                : "bg-ink text-cream hover:bg-terracotta"
            }`}
          >
            Contact
          </Link>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className="flex h-11 w-11 items-center justify-center rounded-full border border-ink/15 text-ink lg:hidden"
          onClick={() => setOpen((v) => !v)}
          aria-label="Menu"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            className="border-t border-stone-border/70 bg-cream/95 backdrop-blur-xl lg:hidden"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="flex flex-col gap-1 px-6 py-6">
              {[...LINKS, { to: "/contact", label: "Contact" }].map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `rounded-xl px-4 py-3 font-serif text-2xl italic transition-colors duration-200 ${
                      isActive ? "bg-terracotta/10 text-terracotta" : "text-ink hover:bg-ink/5"
                    }`
                  }
                >
                  {link.label}
                </NavLink>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
